import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';

interface LoginForm {
  email: string;
  senha: string;
}

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { login, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<LoginForm>();

  // Limpar erro ao montar componente
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Mostrar erro como toast
  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  const onSubmit = async (data: LoginForm) => {
    try {
      setFormError(null);
      await login(data.email, data.senha);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error: any) {
      setFormError(error?.message || 'Erro ao fazer login. Verifique suas credenciais.');
    }
  };

  // Preencher credenciais padrão em desenvolvimento
  const fillDefaultCredentials = () => {
    setValue('email', 'admin@nexus.com');
    setValue('senha', 'admin123');
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Lado esquerdo - Imagem (2/3) */}
      <div className="hidden lg:flex lg:w-2/3 items-center justify-center p-8">
        <img 
          src="/nexus_logo_complete.png" 
          alt="Nexus Funerária" 
          className="max-w-full max-h-full w-auto h-auto object-contain"
        />
      </div>

      {/* Lado direito - Formulário (1/3) */}
      <div className="w-full lg:w-1/3 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-8">
          {/* Header - Mostrar logo em mobile */}
          <div className="text-center lg:hidden">
            <div className="mx-auto mb-4 flex justify-center">
              <img 
                src="/nexus_logo_complete.png" 
                alt="Nexus Funerária" 
                className="h-48 w-auto object-contain"
              />
            </div>
            <p className="text-gray-600">
              Painel Administrativo
            </p>
          </div>

          {/* Header - Desktop */}
          <div className="hidden lg:block text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Painel Administrativo
            </h2>
            <p className="text-gray-600">
              Faça login para acessar o sistema
            </p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {(formError || error) && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {formError || error}
                </div>
              )}

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  {...register('email', {
                    required: 'Email é obrigatório',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email inválido',
                    },
                  })}
                  type="email"
                  id="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="seu@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <input
                    {...register('senha', {
                      required: 'Senha é obrigatória',
                      minLength: {
                        value: 6,
                        message: 'Senha deve ter pelo menos 6 caracteres',
                      },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.senha && (
                  <p className="mt-1 text-sm text-red-600">{errors.senha.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
              </button>

              {/* Credenciais padrão em desenvolvimento */}
              {import.meta.env.DEV && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={fillDefaultCredentials}
                    className="text-sm text-blue-600 hover:text-blue-500 underline"
                  >
                    Preencher credenciais padrão
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    admin@nexus.com / admin123
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-gray-500">
            <p>© 2025 Nexus Funerária. Todos os direitos reservados.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

